import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";
import ThemeToggle from "../ThemeToggle";
import {
  LayoutDashboard, Send, Users, Bot, Inbox, BarChart3, Sparkles, CreditCard, Settings, LogOut, ChevronDown, Plug, BookOpen, Users2, GitBranch, Zap, Linkedin, Network, DollarSign, Flame, Target, Columns, Mail, Handshake, Activity, MessageSquarePlus, Smartphone, Star, Plus, Check, Rss, UserPlus, MessageSquare, Contact,
} from "lucide-react";

const NAV_GROUPS = [
  { section: null, items: [
    { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
    { to: "/app/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
  ]},
  { section: "Find", items: [
    { to: "/app/icp", label: "ICP & Targets", icon: Target, testid: "nav-icp" },
    { to: "/app/leads", label: "Lead Discovery", icon: Sparkles, testid: "nav-leads" },
    { to: "/app/prospects", label: "Prospects", icon: Users, testid: "nav-prospects" },
    { to: "/app/contacts", label: "Contacts", icon: Contact, testid: "nav-contacts" },
    { to: "/app/activation-flow", label: "Activation Flow", icon: GitBranch, testid: "nav-activation-flow" },
    { to: "/app/partnerships", label: "Partnerships", icon: Handshake, testid: "nav-partnerships" },
  ]},
  { section: "Engage", items: [
    { to: "/app/copilot", label: "Sales Copilot", icon: Flame, testid: "nav-copilot" },
    { to: "/app/campaigns", label: "Campaigns", icon: Send, testid: "nav-campaigns" },
    { to: "/app/linkedin", label: "LinkedIn", icon: Linkedin, testid: "nav-linkedin-browser" },
    { to: "/app/linkedin-engage", label: "Page Engagement", icon: MessageSquarePlus, testid: "nav-linkedin-engage" },
    { to: "/app/linkedin-connect", label: "Connect Campaigns", icon: UserPlus, testid: "nav-linkedin-connect" },
  ]},
  { section: "Inbox & Pipeline", items: [
    { to: "/app/email", label: "Inbox", icon: Inbox, testid: "nav-inbox" },
    { to: "/app/sms", label: "SMS Inbox", icon: MessageSquare, testid: "nav-sms" },
    { to: "/app/pipeline", label: "Pipeline", icon: Columns, testid: "nav-pipeline" },
  ]},
  { section: "Automate", items: [
    { to: "/app/agents", label: "AI Agents", icon: Bot, testid: "nav-agents" },
    { to: "/app/workflows", label: "Workflows", icon: GitBranch, testid: "nav-workflows" },
    { to: "/app/autopilot", label: "Autopilot", icon: Zap, testid: "nav-autopilot" },
    { to: "/app/signal-network", label: "Signal Network", icon: Network, testid: "nav-signal" },
  ]},
  { section: "Create & Measure", items: [
    { to: "/app/content", label: "Content Studio", icon: Sparkles, testid: "nav-content" },
    { to: "/app/articles", label: "Articles", icon: BookOpen, testid: "nav-articles" },
    { to: "/app/blog", label: "Blog", icon: Rss, testid: "nav-blog" },
    { to: "/app/reviews", label: "Reviews", icon: Star, testid: "nav-reviews" },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  ]},
];

const SETTINGS_ITEMS = [
  { to: "/app/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
  { to: "/app/help", label: "Help & Setup", icon: BookOpen, testid: "nav-help" },
  { to: "/app/team", label: "Team", icon: Users2, testid: "nav-team" },
  { to: "/app/budget", label: "AI Budget", icon: DollarSign, testid: "nav-budget" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const navItemClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
    isActive
      ? "bg-foreground text-background font-medium"
      : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
  }`;

export default function DashboardLayout({ children, actions, title, eyebrow }) {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();
  const [wsList, setWsList] = useState([]);
  const [wsOpen, setWsOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/workspaces").then(({ data }) => setWsList(data.workspaces || [])).catch(() => {});
  }, []);

  const switchTo = async (id) => {
    if (id === workspace?.id) { setWsOpen(false); return; }
    try {
      const { data } = await api.post("/auth/switch-workspace", { workspace_id: id });
      localStorage.setItem("lg_token", data.token);
      localStorage.setItem("lg_workspace", JSON.stringify(data.workspace));
      toast.success(`Switched to ${data.workspace.name}`);
      window.location.href = "/app";
    } catch { toast.error("Could not switch company"); }
  };

  const addCompany = async () => {
    const name = window.prompt("New company name (e.g. Cargo CFS)");
    if (!name) return;
    const isCargo = name.trim().toLowerCase().replace(/[^a-z]/g, "").includes("cargocfs");
    try {
      const { data } = await api.post("/auth/workspaces", { name: name.trim(), preset: isCargo ? "cargo_cfs" : undefined });
      localStorage.setItem("lg_token", data.token);
      localStorage.setItem("lg_workspace", JSON.stringify(data.workspace));
      toast.success(`Created ${data.workspace.name}`);
      window.location.href = "/app/settings";
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create company"); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/40">
        <div className="px-5 py-5 border-b border-border">
          <Link to="/app" className="flex items-center gap-2" data-testid="sidebar-logo">
            <div className="w-7 h-7 rounded-sm bg-foreground text-background flex items-center justify-center font-black tracking-tighter">L</div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-semibold tracking-tightest">LogiGrowth</span>
              <span className="text-[0.62rem] tracking-[0.22em] uppercase text-muted-foreground">AI Revenue OS</span>
            </div>
          </Link>
        </div>

        {/* Company switcher */}
        <div className="px-3 py-3 border-b border-border relative">
          <button
            data-testid="workspace-switcher-btn"
            onClick={() => setWsOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-border bg-background/50 hover:bg-muted/60 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-sm bg-signal-500/15 text-signal-500 flex items-center justify-center text-xs font-bold border border-signal-500/30">
                {(workspace?.name || "W")[0]}
              </div>
              <div className="text-left min-w-0">
                <div className="text-xs font-semibold truncate">{workspace?.name || "Workspace"}</div>
                <div className="text-[0.62rem] text-muted-foreground uppercase tracking-wider">{workspace?.plan || "starter"} · {workspace?.plan_status || "trial"}</div>
              </div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          {wsOpen && (
            <div className="absolute left-3 right-3 mt-1 z-50 rounded-sm border border-border bg-card shadow-lg overflow-hidden" data-testid="workspace-switcher-menu">
              {wsList.map((w) => (
                <button key={w.id} data-testid={`workspace-option-${w.id}`} onClick={() => switchTo(w.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/60 transition-colors">
                  <span className="text-xs font-medium truncate">{w.name}</span>
                  {w.id === workspace?.id && <Check className="w-3.5 h-3.5 text-signal-500 shrink-0" />}
                </button>
              ))}
              <button onClick={addCompany} data-testid="workspace-add-btn"
                className="w-full flex items-center gap-2 px-3 py-2 text-left border-t border-border hover:bg-muted/60 transition-colors text-signal-500">
                <Plus className="w-3.5 h-3.5" /><span className="text-xs font-medium">Add company</span>
              </button>
            </div>
          )}
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.section || `g${gi}`} className={gi > 0 ? "pt-3" : ""}>
              {group.section && (
                <div className="px-3 pb-1 text-[0.6rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground/60">
                  {group.section}
                </div>
              )}
              {group.items.map(({ to, label, icon: Icon, end, testid }) => (
                <NavLink key={to} to={to} end={end} data-testid={testid} className={navItemClass}>
                  <Icon className="w-4 h-4" /><span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
          <div className="pt-3 mt-3 border-t border-border">
            <div className="px-3 pb-1 text-[0.6rem] font-semibold tracking-[0.2em] uppercase text-muted-foreground/60">
              Workspace
            </div>
            {SETTINGS_ITEMS.map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={to} to={to} data-testid={testid} className={navItemClass}>
                <Icon className="w-4 h-4" /><span>{label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="px-3 py-3 border-t border-border">
          <div className="flex items-center gap-2 px-2 py-2 rounded-sm">
            <div className="w-7 h-7 rounded-full bg-signal-500/15 text-signal-500 border border-signal-500/30 flex items-center justify-center text-xs font-bold">{initial}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[0.65rem] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button
              data-testid="sidebar-logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="w-8 h-8 flex items-center justify-center rounded-sm border border-border hover:border-destructive/40 hover:text-destructive transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="glass sticky top-0 z-30 px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            {eyebrow && <div className="label-eyebrow mb-1" data-testid="page-eyebrow">{eyebrow}</div>}
            <h1 className="text-xl lg:text-2xl font-display font-semibold tracking-tightest" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <Link to="/app/m" title="Switch to mobile command view"
              data-testid="switch-to-mobile-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-sm px-2.5 py-1.5 text-muted-foreground hover:text-foreground hover:border-signal-500 transition-colors">
              <Smartphone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Command view</span>
            </Link>
            <ThemeToggle />
          </div>
        </header>
        <div className="flex-1 px-6 lg:px-10 py-6 lg:py-8 animate-fade-in">{children}</div>
      </main>
    </div>
  );
}
